import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  getDownloadCatalog,
  saveDownload,
  type CatalogApp,
  type DownloadAsset,
} from "../api/accountApi";
import { accountErrorMessage } from "../api/accountErrors";
import { Loading, MetaLine, Notice, Panel, Row, Section } from "../components/Portal";
import { IconDownload } from "../components/icons";
import { useAccountAuth } from "../contexts/AccountAuthContext";
import { APP_STORE_URL } from "../lib/portalCopy";
import {
  bestInstaller,
  detectOs,
  formatBytes,
  formatPublished,
  installerMeta,
  type OsFamily,
} from "../lib/installer";

type LoadState = "loading" | "ready" | "error";

const DEVICES: Array<{ os: OsFamily; title: string; logo: string; detail: string }> = [
  { os: "windows", title: "Windows", logo: "/images/platforms/windows.svg", detail: "64-Bit" },
  { os: "mac", title: "macOS", logo: "/images/platforms/apple.svg", detail: "Universal" },
];

export function DownloadsPage() {
  const { accessToken } = useAccountAuth();
  const os = useMemo(() => detectOs(), []);
  const [apps, setApps] = useState<CatalogApp[]>([]);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyFile, setBusyFile] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadState("loading");
    setError(null);
    try {
      const catalog = await getDownloadCatalog(accessToken);
      setApps(catalog.apps);
      setLoadState("ready");
    } catch (e) {
      setApps([]);
      setError(accountErrorMessage(e));
      setLoadState("error");
    }
  }, [accessToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const download = async (asset: DownloadAsset) => {
    setBusyFile(asset.filename);
    setActionError(null);
    try {
      await saveDownload(asset.download_path, asset.filename, accessToken);
    } catch (e) {
      setActionError(accountErrorMessage(e));
    } finally {
      setBusyFile(null);
    }
  };

  if (loadState === "loading") return <Loading label="Downloads werden geladen" />;

  if (loadState === "error") {
    return (
      <Panel>
        <Row
          title="Katalog nicht verfügbar"
          detail={error}
          action={<button type="button" className="btn-secondary" onClick={() => void load()}>Erneut versuchen</button>}
        />
      </Panel>
    );
  }

  const entitled = apps.filter((app) => app.entitled);
  if (entitled.length === 0) {
    return (
      <Panel>
        <Row
          title="Noch kein Download"
          detail="Die Installer liegen bereit, sobald eine Mitgliedschaft aktiv ist."
          action={<Link to="/billing" className="btn-primary">Mitgliedschaft ansehen</Link>}
        />
      </Panel>
    );
  }

  const latestApp = entitled.find((app) => bestInstaller(app.assets, os));
  const latest = latestApp ? bestInstaller(latestApp.assets, os) : null;
  const device = DEVICES.find((entry) => entry.os === os);
  const notes = latestApp?.notes?.split("\n").map((line) => line.trim()).filter(Boolean) ?? [];

  const button = (asset: DownloadAsset, primary = false) => (
    <button
      type="button"
      className={primary ? "btn-primary" : "btn-secondary btn-icon-sm"}
      disabled={busyFile === asset.filename}
      onClick={() => void download(asset)}
      aria-label={primary ? undefined : `${asset.filename} herunterladen`}
    >
      <IconDownload />
      <span>{busyFile === asset.filename ? "Wird geladen …" : "Herunterladen"}</span>
    </button>
  );

  return (
    <div className="page" data-testid="account-downloads">
      {latest && latestApp && device ? (
        <Panel className="feature">
          <Row
            mark={<img src={device.logo} alt="" />}
            title={`${latestApp.label} für ${device.title}`}
            detail={<MetaLine text={installerMeta(latest, latestApp.published_at) || latest.filename} />}
            action={button(latest, true)}
          />
        </Panel>
      ) : null}

      {actionError ? <Notice tone="error">{actionError}</Notice> : null}

      <Section title="Alle Installer">
        <Panel className="table-panel">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">System</th>
                <th scope="col">Version</th>
                <th scope="col" className="table__wide">Veröffentlicht</th>
                <th scope="col" className="table__wide">Größe</th>
                <th scope="col"><span className="visually-hidden">Aktion</span></th>
              </tr>
            </thead>
            <tbody>
              {entitled.flatMap((app) =>
                DEVICES.flatMap((entry) => {
                  const asset = bestInstaller(app.assets, entry.os);
                  if (!asset) return [];
                  return [
                    <tr key={`${app.app}-${entry.os}`}>
                      <th scope="row">
                        <span className="table__system">
                          <img src={entry.logo} alt="" />
                          <span>
                            {entitled.length > 1 ? `${app.label} · ${entry.title}` : entry.title}
                            <small>{entry.detail}</small>
                          </span>
                        </span>
                      </th>
                      <td className="data">{asset.version || app.version || "—"}</td>
                      <td className="data table__wide">{formatPublished(app.published_at) ?? "—"}</td>
                      <td className="data table__wide">{formatBytes(asset.size_bytes) ?? "—"}</td>
                      <td className="table__action">{button(asset)}</td>
                    </tr>,
                  ];
                }),
              )}
              <tr>
                <th scope="row">
                  <span className="table__system">
                    <img src="/images/platforms/apple.svg" alt="" />
                    <span>
                      iPhone
                      <small>App Store</small>
                    </span>
                  </span>
                </th>
                <td className="data">—</td>
                <td className="data table__wide">—</td>
                <td className="data table__wide">—</td>
                <td className="table__action">
                  <a className="store-badge" href={APP_STORE_URL} target="_blank" rel="noreferrer">
                    <img src="/images/platforms/app-store-de.svg" alt="Laden im App Store" />
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </Panel>
      </Section>

      {notes.length > 0 && latestApp?.version ? (
        <Section title={`Neu in Version ${latestApp.version}`}>
          <ul className="notes">
            {notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </Section>
      ) : null}
    </div>
  );
}
